import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Avatar,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Snackbar,
  Alert
} from '@mui/material';
import { buttonStyles, textFieldStyles } from '../../../Login/LoginPageStyles';
import { PhotoCamera } from '@mui/icons-material';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db, storage } from '../../../firebase';
import AvatarEditorDialog from '../AvatarEditorDialog';

const EditUser = ({ open, onClose, user, onSave }) => {
  const [id, setId] = useState(user.id);
  const [contract, setContract] = useState(user.contract);
  const [cnic, setCnic] = useState(user.cnic);
  const [name, setName] = useState(user.name);
  const [address, setAddress] = useState(user.address);
  const [number, setNumber] = useState(user.address);
  const [position, setPosition] = useState(user.position);
  const [email, setEmail] = useState(user.email);
  const [password, setPassword] = useState(user.password);
  const [role, setRole] = useState(user.role || 'admin');  // Default to 'admin'
  const [weeklyHours, setWeeklyHours] = useState(user.weeklyHours || ''); 
  const [hourlyWage, setHourlyWage] = useState(user.hourlyWage || '');
  const [allowedLeave, setAllowedleave] = useState(user.allowedLeave || '');
  const [avatar, setAvatar] = useState(null);
  const [croppedAvatar, setCroppedAvatar] = useState(user.avatar || null);
  const [avatarEditorOpen, setAvatarEditorOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    setId(user.id);
    setContract(user.contract);
    setCnic(user.cnic);
    setName(user.name);
    setAddress(user.address);
    setNumber(user.number);
    setPosition(user.position);
    setEmail(user.email);
    setPassword(user.password);
    setRole(user.role || 'admin');  
    setWeeklyHours(user.weeklyHours || ''); 
    setHourlyWage(user.hourlyWage || '');
    setAllowedleave(user.allowedLeave || '');
    setCroppedAvatar(user.avatar || null);
    setAvatar(null);
  }, [user]);

  const handleIdChange = (event) => setId(event.target.value);
  const handleContractChange = (event) => setContract(event.target.value);
  const handleNameChange = (event) => setName(event.target.value);
  const handleAddressChange = (event) => setAddress(event.target.value);
  const handleNumberChange = (event) => setNumber(event.target.value);
  const handlePositionChange = (event) => setPosition(event.target.value);
  const handleEmailChange = (event) => setEmail(event.target.value);
  const handlePasswordChange = (event) => setPassword(event.target.value);
  const handleRoleChange = (event) => setRole(event.target.value);  // Handle role change
  const handleWeeklyHoursChange = (event) => setWeeklyHours(event.target.value); // Handle weekly hours change
  const handleHourlyWageChange = (event) => setHourlyWage(event.target.value);
  const handleAllowedLeaveChange = (event) => setAllowedleave(event.target.value);
  const handleAvatarChange = (event) => {
    setAvatar(event.target.files[0]);
    setAvatarEditorOpen(true);
  };

  const handleSaveCroppedAvatar = (croppedImage) => {
    setCroppedAvatar(croppedImage);
    setAvatarEditorOpen(false);
  };

  const handleCnicChange = (e) => {
    let value = e.target.value.replace(/\D/g, ''); // Remove all non-digit characters
  
    if (value.length <= 5) {
      // If the value is 5 or fewer digits, no dash is needed yet
     // value = value;
    } else if (value.length <= 12) {
      // If the value is between 6 and 12 digits, add the first dash
      value = value.slice(0, 5) + '-' + value.slice(5);
    } else {
      // If the value is more than 12 digits, add both dashes
      value = value.slice(0, 5) + '-' + value.slice(5, 12) + '-' + value.slice(12, 13);
    }
  
    setCnic(value); // Update the state with the formatted value
  };
  

  const handleSave = async () => {
    try {
      const updates = {};
      let userDoc = doc(db, 'employees', user.id);

      if (id !== user.id) updates.id = id;
      if (contract !== user.contract) updates.contract = contract;
      if (cnic !== user.cnic) updates.cnic = cnic;
      if (name !== user.name) updates.name = name;
      if (address !== user.address) updates.address = address;
      if (number !== user.number) updates.number = number;
      if (position !== user.position) updates.position = position;
      if (email !== user.email) updates.email = email;
      if (password !== user.password) updates.password = password;
      if (role !== user.role) updates.role = role;  // Include role in updates
      if (weeklyHours !== user.weeklyHours) updates.weeklyHours = weeklyHours; // Include weekly hours in updates
      if (hourlyWage !== user.hourlyWage) updates.hourlyWage = hourlyWage;
      if (allowedLeave !== user.allowedLeave) updates.allowedLeave = allowedLeave;

      if (croppedAvatar && croppedAvatar !== user.avatar) {
        const blob = await (await fetch(croppedAvatar)).blob();
        const avatarRef = ref(storage, `employeeAvatars/${id}`);
        await uploadBytes(avatarRef, blob);
        const avatarUrl = await getDownloadURL(avatarRef);
        updates.avatar = avatarUrl;
      }

      if (id !== user.id) {
        const newUserDoc = doc(db, 'employees', id);
        await setDoc(newUserDoc, { ...user, ...updates });
        await deleteDoc(userDoc);
        userDoc = newUserDoc;
      } else if (Object.keys(updates).length > 0) {
        await updateDoc(userDoc, updates);
      }

      if (Object.keys(updates).length > 0) {
        setSnackbarMessage('User updated successfully!');
        setSnackbarOpen(true);
        onSave({ ...user, ...updates, id });
      } else {
        setSnackbarMessage('No changes detected.');
        setSnackbarOpen(true);
      }

      onClose();
    } catch (error) {
      console.error("Error updating user: ", error);
      setSnackbarMessage('Error updating user.');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Edit Employee</DialogTitle>
        <DialogContent>
          <TextField
            label="ID"
            variant="outlined"
            fullWidth
            value={id}
            onChange={handleIdChange}
            sx={{...textFieldStyles, mb: 2, marginTop: 2 }}
            disabled
          />
          <TextField
            label="Reference/Contract"
            variant="outlined"
            fullWidth
            value={contract}
            onChange={handleContractChange}
            sx={{...textFieldStyles, mb: 2, marginTop: 2 }}
          />
          <TextField
            label="CNIC Number"
            variant="outlined"
            fullWidth
            value={cnic}
            onChange={handleCnicChange}
            sx={{...textFieldStyles, mb: 2, marginTop: 2 }}
            inputProps={{ maxLength: 15 }} // Maximum length for CNIC with dashes (5+7+1+2 = 15)
          />

          <TextField
            label="Name"
            variant="outlined"
            fullWidth
            value={name}
            onChange={handleNameChange}
            sx={{...textFieldStyles, mb: 2, marginTop: 2 }}
          />
          <TextField
            label="Address"
            variant="outlined"
            fullWidth
            value={address}
            onChange={handleAddressChange}
            sx={{...textFieldStyles, mb: 2, marginTop: 2 }}
          />
          <TextField
            label="Phone Number"
            variant="outlined"
            fullWidth
            value={number}
            onChange={handleNumberChange}
            sx={{...textFieldStyles, mb: 2, marginTop: 2 }}
          />
          <TextField
            label="Position"
            variant="outlined"
            fullWidth
            value={position}
            onChange={handlePositionChange}
            sx={{...textFieldStyles, mb: 2, marginTop: 2 }}
          />
          <TextField
            label="Email"
            variant="outlined"
            fullWidth
            value={email}
            onChange={handleEmailChange}
            sx={{...textFieldStyles, mb: 2, marginTop: 2 }}
          />
          <TextField
            label="Password"
            variant="outlined"
            fullWidth
            value={password}
            onChange={handlePasswordChange}
            sx={{...textFieldStyles, mb: 2, marginTop: 2 }}
          />
          <TextField
            label="Weekly Hours"
            variant="outlined"
            fullWidth
            value={weeklyHours}
            onChange={handleWeeklyHoursChange} // Handle weekly hours change
            sx={{...textFieldStyles, mb: 2, marginTop: 2 }}
          />
          <TextField
            label="Hourly Wage"
            variant="outlined"
            fullWidth
            value={hourlyWage}
            onChange={handleHourlyWageChange} // Handle weekly hours change
            sx={{...textFieldStyles, mb: 2, marginTop: 2 }}
          />
          <TextField
            label="Allowed Paid Leave"
            variant="outlined"
            fullWidth
            value={allowedLeave}
            onChange={handleAllowedLeaveChange} // Handle weekly hours change
            sx={{...textFieldStyles, mb: 2, marginTop: 2 }}
          />
          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <RadioGroup row value={role} onChange={handleRoleChange}>
              <FormControlLabel value="admin" control={<Radio />} label="Admin" />
              <FormControlLabel value="employee" control={<Radio />} label="Employee" />
            </RadioGroup>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Button variant="contained" component="label" 
              sx={{...buttonStyles, mt:0}}>
              Upload Picture
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleAvatarChange}
              />
              <IconButton component="span">
                <PhotoCamera />
              </IconButton>
            </Button>
            {croppedAvatar && (
              <Avatar
                src={croppedAvatar}
                sx={{ 
                  mt: 2,
                  width: 56, 
                  height: 56,
                  objectFit: 'cover',
                  alignItems: 'center',
                }}
              />
            )}
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} sx={{ color: 'red' }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" 
            sx={{...buttonStyles, mt: 0 }}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarMessage.includes('Error') ? 'error' : 'success'}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <AvatarEditorDialog
        open={avatarEditorOpen}
        onClose={() => setAvatarEditorOpen(false)}
        onSave={handleSaveCroppedAvatar}
        image={avatar}
      />
    </>
  );
};

export default EditUser;
